import React from 'react';
import { i18n } from 'config/locales';
import Field from 'components/Form/Field';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import { ForeignActivitiesBenefitItemModel } from 'forms/sections/foreign/activities/benefits/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonScrollIntoView
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import { CheckboxModel } from 'forms/shared/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import OtherBenefit from './OtherBenefit';
import ContinuingBenefit from './ContinuingBenefit';
import FutureBenefit from './FutureBenefit';
import OneTimeBenefit from './OneTimeBenefit';

interface Props extends Partial<ForeignActivitiesBenefitItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class Benefit extends ValidationElement<Props> {
  update = (queue: Partial<ForeignActivitiesBenefitItemModel>) => {
    this.props.onUpdate({
      InterestTypes: this.props.InterestTypes,
      BenefitType: this.props.BenefitType,
      OtherBenefitType: this.props.OtherBenefitType,
      BenefitFrequency: this.props.BenefitFrequency,
      OneTimeBenefit: this.props.OneTimeBenefit,
      FutureBenefit: this.props.FutureBenefit,
      ContinuingBenefit: this.props.ContinuingBenefit,
      OtherBenefit: this.props.OtherBenefit,
      ...queue,
    });
  };

  updateInterestTypes = (values: CheckboxModel) => {
    const interestType = values.value;
    const selected = [...((this.props.InterestTypes || {}).values || [])];
    if (selected.includes(interestType)) {
      selected.splice(selected.indexOf(interestType), 1);
    } else {
      selected.push(interestType);
    }

    this.update({
      InterestTypes: { values: selected },
    });
  };

  updateBenefitType = (values: ForeignActivitiesBenefitItemModel['BenefitType']) => {
    this.update({
      BenefitType: values,
    });
  };

  updateOtherBenefitType = (values: ForeignActivitiesBenefitItemModel['OtherBenefitType']) => {
    this.update({
      OtherBenefitType: values,
    });
  };

  updateBenefitFrequency = (values: ForeignActivitiesBenefitItemModel['BenefitFrequency']) => {
    this.update({
      BenefitFrequency: values,
    });
  };

  updateOneTimeBenefit = (values: ForeignActivitiesBenefitItemModel['OneTimeBenefit']) => {
    this.update({
      OneTimeBenefit: values,
    });
  };

  updateFutureBenefit = (values: ForeignActivitiesBenefitItemModel['FutureBenefit']) => {
    this.update({
      FutureBenefit: values,
    });
  };

  updateContinuingBenefit = (values: ForeignActivitiesBenefitItemModel['ContinuingBenefit']) => {
    this.update({
      ContinuingBenefit: values,
    });
  };

  updateOtherBenefit = (values: ForeignActivitiesBenefitItemModel['OtherBenefit']) => {
    this.update({
      OtherBenefit: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="benefit">
        <Field
          title={i18n.t('foreign.activities.benefit.heading.interestTypes')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestTypes`}
        >
          <p>{i18n.t('foreign.activities.benefit.para.checkAll')}</p>
          <CheckboxGroup
            className="interest-types option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.InterestTypes || {}).values}
            attribute={`${attribute}.InterestTypes`}
          >
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.benefit.label.interestTypes.yourself')}
              value="Yourself"
              className="yourself"
              onUpdate={this.updateInterestTypes}
              onError={this.props.onError}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.benefit.label.interestTypes.spouse')}
              value="Spouse"
              className="spouse"
              onUpdate={this.updateInterestTypes}
              onError={this.props.onError}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.benefit.label.interestTypes.cohabitant')}
              value="Cohabitant"
              className="cohabitant"
              onUpdate={this.updateInterestTypes}
              onError={this.props.onError}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.benefit.label.interestTypes.dependentChildren')}
              value="DependentChildren"
              className="dependent-children"
              onUpdate={this.updateInterestTypes}
              onError={this.props.onError}
            />
          </CheckboxGroup>
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.heading.benefitType')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BenefitType`}
        >
          <RadioGroup
            className="benefit-types option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={(this.props.BenefitType || {}).value}
            attribute={`${attribute}.BenefitType`}
          >
            <Radio
              name="benefit_type"
              label={i18n.t('foreign.activities.benefit.label.benefitTypes.educational')}
              value="Educational"
              onUpdate={this.updateBenefitType}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_type"
              label={i18n.t('foreign.activities.benefit.label.benefitTypes.medical')}
              value="Medical"
              onUpdate={this.updateBenefitType}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_type"
              label={i18n.t('foreign.activities.benefit.label.benefitTypes.retirement')}
              value="Retirement"
              onUpdate={this.updateBenefitType}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_type"
              label={i18n.t('foreign.activities.benefit.label.benefitTypes.socialWelfare')}
              value="SocialWelfare"
              onUpdate={this.updateBenefitType}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_type"
              label={i18n.t('foreign.activities.benefit.label.benefitTypes.other')}
              value="Other"
              className="benefit-other"
              onUpdate={this.updateBenefitType}
              onError={this.props.onError}
            />
          </RadioGroup>
          <Show when={(this.props.BenefitType || {}).value === 'Other'}>
            <div>
              {i18n.t('foreign.activities.benefit.label.otherBenefitType')}
              <Field attribute={`${attribute}.OtherBenefitType`}>
                <Textarea
                  name="OtherBenefitType"
                  {...this.props.OtherBenefitType}
                  onUpdate={this.updateOtherBenefitType}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.OtherBenefitType`}
                />
              </Field>
            </div>
          </Show>
        </Field>

        <Field
          title={i18n.t('foreign.activities.benefit.heading.benefitFrequency')}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.BenefitFrequency`}
        >
          <RadioGroup
            className="benefit-frequency option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={(this.props.BenefitFrequency || {}).value}
            attribute={`${attribute}.BenefitFrequency`}
          >
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.label.benefitFrequency.oneTime')}
              value="OneTime"
              onUpdate={this.updateBenefitFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.label.benefitFrequency.future')}
              value="Future"
              onUpdate={this.updateBenefitFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.label.benefitFrequency.continuing')}
              value="Continuing"
              onUpdate={this.updateBenefitFrequency}
              onError={this.props.onError}
            />
            <Radio
              name="benefit_frequency"
              label={i18n.t('foreign.activities.benefit.label.benefitFrequency.other')}
              value="Other"
              onUpdate={this.updateBenefitFrequency}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>

        <Show when={(this.props.BenefitFrequency || {}).value === 'OneTime'}>
          <OneTimeBenefit
            name="OneTimeBenefit"
            {...this.props.OneTimeBenefit}
            onUpdate={this.updateOneTimeBenefit}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.OneTimeBenefit`}
          />
        </Show>

        <Show when={(this.props.BenefitFrequency || {}).value === 'Future'}>
          <FutureBenefit
            name="FutureBenefit"
            {...this.props.FutureBenefit}
            onUpdate={this.updateFutureBenefit}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.FutureBenefit`}
          />
        </Show>

        <Show when={(this.props.BenefitFrequency || {}).value === 'Continuing'}>
          <ContinuingBenefit
            name="ContinuingBenefit"
            {...this.props.ContinuingBenefit}
            onUpdate={this.updateContinuingBenefit}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ContinuingBenefit`}
          />
        </Show>

        <Show when={(this.props.BenefitFrequency || {}).value === 'Other'}>
          <OtherBenefit
            name="OtherBenefit"
            otherBenefit={this.props.OtherBenefit || {}}
            onUpdate={this.updateOtherBenefit}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.OtherBenefit`}
          />
        </Show>
      </div>
    );
  }
}

Benefit.defaultProps = {
  InterestTypes: {},
  BenefitType: {},
  BenefitFrequency: {},
  onUpdate: (queue) => {},
  onError: (value, arr: ErrorCode) => arr,
};
